import { type ReactNode, useMemo } from 'react';
import { InfinityLoading } from '../infinity-loading';
import { Select, type SelectProperties } from '../select';
import { useCombineOptions } from './hooks';

type P<ValueType extends { label: ReactNode; value: string }, OptionType extends ValueType> = {
  fullWidth?: boolean;
  handleRequestMoreData: () => void;
  hasMoreData?: boolean;
  isRefetching?: boolean;
  onChange?: (value?: ValueType | ValueType[]) => void;
} & Omit<SelectProperties<ValueType, OptionType>, 'labelInValue' | 'onChange'>;

const InfiniteScrollSelect = <
  ValueType extends { label: ReactNode; value: string },
  OptionType extends ValueType = ValueType,
>({
  handleRequestMoreData,
  hasMoreData,
  isRefetching = false,
  onChange,
  ...rest
}: P<ValueType, OptionType>) => {
  const combineOptions = useCombineOptions();
  const { fieldNames, loading: isLoading, mode, options: selectOptions } = rest;
  const options = useMemo(() => {
    if (isRefetching) {
      return undefined;
    }

    const allOptions: OptionType[] = selectOptions ? [...selectOptions] : [];
    if (Boolean(isLoading) || hasMoreData) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- // This is just for TS check, this option will never be selected it is just UI loading representation
      allOptions.push({
        label: (
          <InfinityLoading
            hasMoreData={hasMoreData}
            isLoading={isLoading ?? false}
            onEnter={handleRequestMoreData}
            spinnerSize="default"
          />
        ),
        value: '',
        ...(fieldNames?.label
          ? {
              [fieldNames.label]: (
                <InfinityLoading
                  hasMoreData={hasMoreData}
                  isLoading={isLoading ?? false}
                  onEnter={handleRequestMoreData}
                  spinnerSize="default"
                />
              ),
            }
          : {}),
        disabled: true,
      } as unknown as OptionType);
    }

    return allOptions;
  }, [fieldNames, handleRequestMoreData, hasMoreData, isRefetching, isLoading, selectOptions]);

  return (
    <Select<ValueType, OptionType>
      onChange={(value, option) => {
        onChange?.(mode === 'multiple' ? combineOptions(value, option, value) : option);
      }}
      showSearch={{
        filterOption: false,
      }}
      {...rest}
      labelInValue
      loading={isRefetching || isLoading}
      options={options}
    />
  );
};

export default InfiniteScrollSelect;
